import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from '../../components';
import {
  nameSoon,
  nameOverdue,
  addBucketNameToTasks,
  compareTasksOverdueFirst,
} from '../../fleet-shared/TaskBuckets.mjs';
import {
  urlForTaskId,
  urlForTasks,
  bucketNameAll,
  bucketNameOverdue,
  bucketNameSoon,
} from '../../fleet-shared/Urls.mjs';
import { client } from '../../utilities/Database.mjs';
import { useStatusMessage } from '../../components';
import { useIsMounted } from '../../utilities/useMounted';

const MAX_TASKS_SHOWN = 5;

const fmt = (n) => n.toLocaleString('en-US');

// Stop clicks inside the summary from bubbling to ItemRow's onClick, which
// would navigate to the item detail page instead of the tasks list.
const stop = (e) => e.stopPropagation();

// A bucket's tinted panel. Its header is a link carrying the count (e.g.
// "3 Overdue") that navigates to that bucket's task list; below it are up to
// MAX_TASKS_SHOWN task rows and a "See N more" link. Tinted per bucket via
// `--tint-rgb`. Renders nothing when the bucket has no tasks.
const TaskPanel = ({ itemId, count, label, labelClass, tasks, bucketUrl, toneRgb }) => {
  if (count === 0) {
    return null;
  }
  const shown = tasks.slice(0, MAX_TASKS_SHOWN);
  const more = tasks.length - shown.length;

  return (
    <ul
      className="list-unstyled task-inline-list mt-1 mb-2"
      style={{ '--tint-rgb': toneRgb }}
    >
      <li>
        <Link
          to={urlForTasks(itemId, bucketUrl)}
          onClick={stop}
          className={`task-inline-row task-inline-label small fw-bold ${labelClass}`}
        >
          <span>{fmt(count)} {label}</span>
          <i className="fa-solid fa-chevron-right" aria-hidden="true" />
        </Link>
      </li>
      {shown.map((t) => (
        <li key={t.id}>
          <Link
            to={urlForTaskId(itemId, t.id)}
            onClick={stop}
            className="task-inline-row"
          >
            <span className="entered-data">{t.name}</span>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
          </Link>
        </li>
      ))}
      {more > 0 && (
        <li>
          <Link to={urlForTasks(itemId, bucketUrl)} onClick={stop}>
            See {more} more
          </Link>
        </li>
      )}
    </ul>
  );
};

// `soonDays` / `soonCounter` are the item's due-soon window (null = default).
// The raw task list is what the fetch stores; the buckets are derived from it
// and the window in a memo, so an edit to the window from the Items page's
// modal re-buckets the row without another read.
export const TaskSummary = ({ itemId, soonDays = null, soonCounter = null }) => {
  const [tasks, setTasks] = useState(null);
  const [StatusMessage, {showError, hideStatus}] = useStatusMessage();
  const isMounted = useIsMounted();

  // Lists all tasks for an item. Returns the raw task array.
  const listTasksAndCountersForItemId = useCallback(async (itemId) =>  {
    // GraphQL to get id/name plus due-counter/date info for an item's tasks.
    const graphQL_TaskDueInformationByItemId = `
      query TasksByItemId(
        $itemId: ID!,
        $nextToken: String
      ) {
        tasksByItemIdAndName(
          itemId: $itemId,
          nextToken: $nextToken
        ) {
          items {
            id
            name
            dueCounter
            dueDate
            itemCounter {
              counter
            }
          }
          nextToken
        }
      }
    `;

    console.log(`Fetching tasks/counters for ${itemId}`);

    hideStatus();

    let nextToken = null;
    let allTasks = [];

    // Get all the tasks for the item. Keep calling until we have them all (when nextToken is null).
    do {
      let fetch = await client.graphql({query: graphQL_TaskDueInformationByItemId, variables: { itemId: itemId, nextToken: nextToken}});

      let tasks = fetch.data.tasksByItemIdAndName.items;
      allTasks = allTasks.concat(tasks);
      nextToken = fetch.data.tasksByItemIdAndName.nextToken;

      if (nextToken) {
        console.log(`Fetching more tasks for ${itemId}`);
      }
    } while (nextToken);

    return allTasks;
  }
  , [hideStatus]);

  useEffect(() => {
    // Fetch tasks for count + the inline task lists.
    async function fetchTasks() {
      try {
        let tasks = await listTasksAndCountersForItemId(itemId);

        if (!isMounted()) {
          console.error(`${itemId} is not mounted?`);
          return;
        }

        setTasks(tasks);
      } catch (err) {
        if (!isMounted()) {
          return;
        }
        showError(err);
      }
    }

    fetchTasks();
  }, [isMounted, itemId, listTasksAndCountersForItemId, showError]);

  // Tag each task with its first-matching bucket (Overdue → Soon → All) over
  // this item's window, then derive per-bucket lists. The overdue/soon
  // panels' counts match their lists exactly. The "All" row keeps the
  // grand-total count but lists no tasks -- overdue/soon tasks already show
  // in their own panels, and the count links through to the full task list.
  const buckets = useMemo(() => {
    if (!tasks) {
      return null;
    }
    const tagged = tasks.map((t) => ({ ...t }));
    addBucketNameToTasks(tagged, { soonDays, soonCounter });
    const sorted = tagged.sort(compareTasksOverdueFirst);
    const overdueTasks = sorted.filter((t) => t.bucketName === nameOverdue);
    const soonTasks = sorted.filter((t) => t.bucketName === nameSoon);

    return {
      total: { count: sorted.length },
      overdue: { count: overdueTasks.length, tasks: overdueTasks },
      soon: { count: soonTasks.length, tasks: soonTasks },
    };
  }, [tasks, soonDays, soonCounter]);

  if (buckets) {
    return (
      <div className="task-summary">
        <StatusMessage />
        <div className="task-summary-lists">
          <TaskPanel
            itemId={itemId}
            count={buckets.overdue.count}
            label="Overdue"
            labelClass="text-danger"
            tasks={buckets.overdue.tasks}
            bucketUrl={bucketNameOverdue}
            toneRgb="var(--bs-danger-rgb)"
          />
          <TaskPanel
            itemId={itemId}
            count={buckets.soon.count}
            label="Due soon"
            labelClass="text-warning"
            tasks={buckets.soon.tasks}
            bucketUrl={bucketNameSoon}
            toneRgb="var(--bs-warning-rgb)"
          />
          <ul
            className="list-unstyled task-inline-list mt-1 mb-2"
            style={{ '--tint-rgb': 'var(--bs-body-color-rgb)' }}
          >
            <li>
              <Link
                to={urlForTasks(itemId, bucketNameAll)}
                onClick={stop}
                className="task-inline-row task-inline-label small fw-bold text-secondary"
              >
                <span>All {fmt(buckets.total.count)} tasks</span>
                <i className="fa-solid fa-chevron-right" aria-hidden="true" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    );
  } else {
    return (
      <span>
        <StatusMessage />
        <Spinner /><span className='p-2 text-secondary'>Loading tasks...</span>
      </span>
    );
  }
};
